<script setup>
import {
  ref, computed, inject, watch,
} from 'vue';
import usuarios from '@/api/usuarios';
import Parser, { OPERADORES } from '@/libs/cifra/formulas/Parser';
import TokenOperador from '@/libs/cifra/formulas/TokenOperador';
import TokenNumero from '@/libs/cifra/formulas/TokenNumero';
import TokenVariable from '@/libs/cifra/formulas/TokenVariable';
import { propsEditor } from './editores_mixins';

const I18n = inject('I18n');

const props = defineProps(propsEditor());
const emits = defineEmits(['update:modelValue']);

const NUMEROS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0, ','];
const tokens = ref([]);

if (props.modelValue?.formula) {
  const parser = new Parser(props.variablesDisponibles);
  try {
    tokens.value = parser.parse(props.modelValue.formula);
  } catch (error) {
    console.error(error);
  }
}

const formulaInterna = computed(() => (
  tokens.value.map((token) => token.valor).join(' ')
));

const variablesUsadas = computed(() => (
  tokens.value.filter(
    (token) => (token instanceof TokenVariable),
  ).map((token) => token.variable.data)
));

function tooltipVariable(variable) {
  let tooltip = null;
  if (variable.pestana) {
    tooltip = variable.pestana.nombre;
    if (variable.de_otro_usuario) {
      tooltip = `${tooltip} ${usuarios.fullname(variable.usuario)}`;
    }
  } else if (variable.es_nota_final) {
    tooltip = I18n.t('Nota_final');
    if (variable.asignatura) {
      tooltip = `${tooltip} ${variable.asignatura.nombre}`;
    }
  } else if (variable.es_objetivo) {
    tooltip = `${I18n.t('Objetivo')} ${variable.evaluacion.nombre}`;
  }
  return tooltip;
}
function showIconoUsuario(variable) {
  return !!variable.de_otro_usuario;
}

function notificarCambio() {
  const nuevo = { ...props.modelValue };
  nuevo.tipo = 'manual';
  nuevo.formula = formulaInterna.value;
  nuevo.formula_variables = variablesUsadas.value;

  emits('update:modelValue', nuevo);
}

watch(tokens, notificarCambio, { deep: true });

function addToken(token) {
  const ultimo = tokens.value[tokens.value.length - 1];

  // Si hay token anterior, comprobamos que el nuevo es válido
  if (ultimo && !ultimo.siguienteValido(token)) {
    return;
  }
  tokens.value.push(token);
}

function insertarVariable(variable) {
  addToken(new TokenVariable(variable));
}

function insertarOperador(op) {
  addToken(new TokenOperador(op));
}

function insertNumero(valor) {
  let token = tokens.value[tokens.value.length - 1];
  const ultimoEsNumerico = token instanceof TokenNumero;

  if (!token || !ultimoEsNumerico) {
    token = new TokenNumero();
    addToken(token);
  }
  token.concat(valor);
}

function eliminarToken() {
  tokens.value.pop();
}

function eliminarTodo() {
  tokens.value = [];
}
</script>

<template>
  <div>
    <h3 class="m-b">
      {{ $I18n.t('Formula') }}
    </h3>
    <div class="formula-display">
      <span v-if="!tokens.length">&nbsp;</span>
      <span
        v-for="(token, index) in tokens"
        :key="`token_${index}`"
        class="label label-white"
      >
        {{ token.label }}
      </span>
    </div>
    <div class="hide">
      {{ formulaInterna }}
    </div>
    <div class="botoneras">
      <div class="btn-group botonera">
        <button
          v-for="op in OPERADORES"
          :key="op"
          type="button"
          class="btn btn-default"
          @click="insertarOperador(op)"
        >
          {{ op }}
        </button>
        <button
          type="button"
          class="btn btn-warning btn-2-spaces"
          data-toggle="tooltip"
          :title="$I18n.t('borrar_ultimo')"
          @click="eliminarToken()"
        >
          <i class="far fa-backspace"></i>
        </button>
        <button
          type="button"
          class="btn btn-danger btn-2-spaces"
          data-toggle="tooltip"
          :title="$I18n.t('borrar_todo')"
          @click="eliminarTodo()"
        >
          <i class="far fa-eraser"></i>
        </button>
      </div>
      <div class="btn-group botonera">
        <button
          v-for="num in NUMEROS"
          :key="num"
          type="button"
          class="btn btn-default"
          @click="insertNumero(num)"
        >
          {{ num }}
        </button>
      </div>
    </div>
    <div class="items">
      <h5>{{ $I18n.t('Items') }}</h5>
      <button
        v-for="variable in variablesDisponibles"
        :key="variable.key"
        v-tooltip="tooltipVariable(variable)"
        type="button"
        class="btn btn-white btn-xs m-r-xs"
        @click="insertarVariable(variable)"
      >
        {{ variable.label }}
        <i
          v-if="showIconoUsuario(variable)"
          class="fa fa-user"
        >
        </i>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "@/../assets/stylesheets/cifra/vars/main.scss";

  $btn-width: 38px;
  $btn-margin-right: 1px;

  .botoneras {
    display: flex;
    flex-flow: column;
    align-items: center;
    margin-top: 5px;
  }

  .botonera {
    margin-top: 2px;

    .btn {
      width: $btn-width;
      max-width: $btn-width;
      margin: 0 $btn-margin-right $btn-margin-right 0;

      &.btn-2-spaces {
        width: $btn-width * 2 + $btn-margin-right;
        max-width: $btn-width * 2 + $btn-margin-right;
      }
    }
  }

  .items {
    line-height: 2em;
    margin-top: 10px;
    margin-bottom: 4px;

    .btn {
      white-space: normal;
    }
  }

  .formula-display {
    background-color: $light-gray;
    padding: 10px 10px;
    border: 1px solid $border-color;
    line-height: 1.4em;

    span {
      display: inline-block;
      font-size: 14px;
      font-weight: normal;
      line-height: 1.4em;
      text-align: center;

      &.label {
        padding: 6px 12px;
        border: 1px solid $border-color;
      }
      &:not(.label) {
        // El mismo padding que las label más los bordes
        padding: 7px 13px;
      }
    }
  }
</style>
